import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import Attachment from "../types/attachment";
import SearchResults from "../types/searchResults";
import SearchParams from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";

interface AttachmentState {
  attachment: Attachment;
  attachmentSearchResults?: SearchResults;
  addedAttachments: File[];
  siteAttachments: Attachment[];
  jobAttachments: Attachment[];
  tempAttachments: Attachment[];
  status: "idle" | "loading" | "failed";
}

const initialState: AttachmentState = {
  attachment: {
    id: 0,
    name: "",
  },
  attachmentSearchResults: undefined,
  addedAttachments: [],
  siteAttachments: [],
  jobAttachments: [],
  tempAttachments: [],
  status: "idle",
};

const buildFormData = (formData: any, data: any, parentKey?: any) => {
  if (
    data &&
    typeof data === "object" &&
    !(data instanceof Date) &&
    !(data instanceof File)
  ) {
    Object.keys(data).forEach((key) => {
      buildFormData(
        formData,
        data[key],
        parentKey ? `${parentKey}[${key}]` : key
      );
    });
  } else {
    const value = data == null || data === undefined ? null : data;

    if (!!data) {
      formData.append(parentKey, value);
    }
  }

  return formData;
};

const jsonToFormData = (data: any) => {
  const formData = new FormData();
  const newData = buildFormData(formData, data);
  return newData;
};

export const getAttachmentId = createAsyncThunk<Attachment, number>(
  "attachment/get/id",
  async (id: number) => {
    const response = await http.get<Attachment>(`/attachment/${id}`);
    return response.data;
  }
);

export const editAttachment = createAsyncThunk<Attachment, Attachment>(
  "attachment/edit",
  async (attachment: Attachment) => {
    var form = jsonToFormData(attachment);

    if (!!attachment?.attachmentType?.id) {
      form.append("attachmentType.id", attachment.attachmentType.id);
    }

    if (attachment.jobId) {
      form.append("job.id", attachment.jobId);
    }

    form.append("name", attachment.name);

    const config = {
      headers: { "Content-Type": "multipart/form-data" },
      params: {
        successToast: true,
      },
    };

    const response = await http.put<Attachment>(
      `/attachment/${attachment.id}`,
      form,
      config
    );
    return response.data;
  }
);

export const deleteAttachment = createAsyncThunk<Attachment, number>(
  "attachment/delete",
  async (id: number) => {
    const response = await http.delete<Attachment>(`/attachment/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const postAttachment = createAsyncThunk<number, Attachment>(
  "attachment/post",
  async (attachment: Attachment) => {
    var form = jsonToFormData(attachment);

    if (attachment.upload) {
      form.append("upload", attachment.upload);
    }

    if (attachment.siteId) {
      form.append("site.id", attachment.siteId);
    }

    if (attachment.jobId) {
      form.append("job.id", attachment.jobId);
    }
    if (attachment.attachmentType) {
      form.append("attachmentType.id", attachment.attachmentType.id);
      form.append("attachmentType.value", attachment.attachmentType.name);
    } else {
      form.append("attachmentType.id", "");
      form.append("attachmentType.value", "");
    }

    const config = {
      headers: { "Content-Type": "multipart/form-data" },
      params: { successToast: true },
    };

    const response = await http.post(`/attachment`, form, config);
    return response.data;
  }
);

export const searchAttachments = createAsyncThunk<SearchResults, SearchParams>(
  "attachment/search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(`/attachment/search?${qs}`);
    return response.data;
  }
);

export const getSiteAttachments = createAsyncThunk<Attachment[], number>(
  "attachments/site",
  async (id: number) => {
    const result = await http.get<Attachment[]>(
      `/attachment/site-attachments?siteId=${id}`
    );
    return result.data;
  }
);

export const getJobAttachments = createAsyncThunk<Attachment[], number>(
  "attachments/job",
  async (id: number) => {
    const result = await http.get<Attachment[]>(
      `/attachment/job-attachments?jobId=${id}`
    );
    return result.data;
  }
);

export const copyAttachmentsToJob = createAsyncThunk<
  null,
  { files: Attachment[]; jobId: number }
>("attachments/copy", async ({ files, jobId }) => {
  const result = await http.patch(`/attachment/copy-to/${jobId}`, files, {
    params: {
      successToast: true,
    },
  });
  return result.data;
});

const attachmentSlice = createSlice({
  name: "attachment",
  initialState,
  reducers: {
    resetAttachment(state) {
      state.attachment = { ...initialState.attachment };
    },
    setAddedAttachments(state, action) {
      state.addedAttachments = action.payload;
    },
    resetJobAttachments(state) {
      state.jobAttachments = [];
    },
    setTempAttachments(state, action) {
      state.tempAttachments = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAttachmentId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAttachmentId.fulfilled, (state, action) => {
        state.status = "idle";
        state.attachment = action.payload;
      })
      .addCase(getAttachmentId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editAttachment.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editAttachment.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(editAttachment.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteAttachment.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteAttachment.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteAttachment.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postAttachment.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postAttachment.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(postAttachment.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchAttachments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchAttachments.fulfilled, (state, action) => {
        state.attachmentSearchResults = action.payload;
        state.status = "idle";
      })
      .addCase(searchAttachments.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getSiteAttachments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSiteAttachments.fulfilled, (state, action) => {
        state.status = "idle";
        state.siteAttachments = action.payload;
      })
      .addCase(getSiteAttachments.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getJobAttachments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getJobAttachments.fulfilled, (state, action) => {
        state.status = "idle";
        state.jobAttachments = action.payload;
      })
      .addCase(getJobAttachments.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(copyAttachmentsToJob.pending, (state) => {
        state.status = "loading";
      })
      .addCase(copyAttachmentsToJob.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(copyAttachmentsToJob.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default attachmentSlice.reducer;
export const {
  resetAttachment,
  setAddedAttachments,
  resetJobAttachments,
  setTempAttachments,
} = attachmentSlice.actions;
